import { db } from '../database';
import { exerciseLibrary } from '../data/exerciseLibrary';

export async function seedExerciseLibrary(): Promise<void> {
  try {
    // Check if exercises already exist
    const existingCount = await db.exercises.count();
    if (existingCount > 0) {
      console.log(`${existingCount} exercises already exist. Skipping seed.`);
      return;
    }

    console.log('Seeding exercise library...');
    const now = new Date();

    // Add all exercises from the library
    const exercisesToAdd = exerciseLibrary.map(exercise => ({
      ...exercise,
      createdAt: now,
      updatedAt: now
    }));

    // Use bulkPut to insert with specific IDs instead of auto-generating
    await db.exercises.bulkPut(exercisesToAdd);
    
    console.log(`Successfully seeded ${exercisesToAdd.length} exercises!`);
  } catch (error) {
    console.error('Failed to seed exercise library:', error);
    throw error;
  }
}

/**
 * Add new exercises from the library that don't exist in the database yet.
 * Safe to run multiple times - only adds exercises that are missing.
 */
export async function addNewExercisesFromLibrary(): Promise<void> {
  try {
    console.log('Checking for new exercises to add...');
    
    // Get all existing exercises from database
    const existingExercises = await db.exercises.toArray();
    const existingNames = new Set(existingExercises.map(ex => ex.name.toLowerCase()));
    
    // Find exercises in library that don't exist in database
    const newExercises = exerciseLibrary.filter(libExercise => 
      !existingNames.has(libExercise.name.toLowerCase())
    );
    
    if (newExercises.length === 0) {
      console.log('No new exercises to add. Database is up to date!');
      return;
    }
    
    console.log(`Found ${newExercises.length} new exercises to add...`);
    const now = new Date();
    
    // Add new exercises to database
    const exercisesToAdd = newExercises.map(exercise => ({
      ...exercise,
      createdAt: now,
      updatedAt: now
    }));
    
    // Use bulkPut to insert with specific IDs instead of auto-generating
    await db.exercises.bulkPut(exercisesToAdd);
    
    console.log(`Successfully added ${newExercises.length} new exercises!`);
    console.log('New exercises:', newExercises.map(ex => ex.name));
    
  } catch (error) {
    console.error('Failed to add new exercises:', error);
    throw error;
  }
}

export async function clearAndReseedExercises(): Promise<void> {
  try {
    console.log('Clearing existing exercises...');
    await db.exercises.clear();
    
    console.log('Reseeding exercise library...');
    await seedExerciseLibrary();
  } catch (error) {
    console.error('Failed to clear and reseed exercises:', error);
    throw error;
  }
}

export async function seedTrainingDays(): Promise<void> {
  try {
    // Check if training days already exist
    const existingCount = await db.trainingDays.count();
    if (existingCount > 0) {
      console.log(`${existingCount} training days already exist. Skipping seed.`);
      return;
    }

    console.log('Seeding training days...');
    const now = new Date();

    // Get some exercises for the training days - if none exist, seed them first
    let exercises = await db.exercises.toArray();
    if (exercises.length === 0) {
      console.log('No exercises found. Seeding exercise library first...');
      await seedExerciseLibrary();
      exercises = await db.exercises.toArray();
    }

    // Helper to find exercise by name
    const findExercise = (name: string) => exercises.find(e => e.name.toLowerCase().includes(name.toLowerCase()));

    // Training Day 1: Upper Body Push
    const upperPushId = await db.trainingDays.add({
      name: 'Upper Body Push',
      exercises: [],
      createdAt: now,
      updatedAt: now
    });

    // Add exercises to Upper Push
    const pushExercises = [
      { exercise: findExercise('bench press') || findExercise('push'), weight: 135, sets: [{ targetReps: 8, order: 0 }, { targetReps: 8, order: 1 }, { targetReps: 8, order: 2 }] },
      { exercise: findExercise('shoulder press') || findExercise('press'), weight: 75, sets: [{ targetReps: 10, order: 0 }, { targetReps: 10, order: 1 }, { targetReps: 10, order: 2 }] },
      { exercise: findExercise('tricep'), weight: 60, sets: [{ targetReps: 12, order: 0 }, { targetReps: 12, order: 1 }] }
    ];

    for (let i = 0; i < pushExercises.length; i++) {
      const ex = pushExercises[i];
      if (ex.exercise) {
        await db.trainingDayExercises.add({
          trainingDayId: upperPushId,
          exerciseId: ex.exercise.id!,
          weight: ex.weight,
          sets: ex.sets,
          order: i,
          discoveryMode: false
        });
      }
    }

    // Training Day 2: Upper Body Pull
    const upperPullId = await db.trainingDays.add({
      name: 'Upper Body Pull',
      exercises: [],
      createdAt: now,
      updatedAt: now
    });

    const pullExercises = [
      { exercise: findExercise('pull') || findExercise('row'), weight: 120, sets: [{ targetReps: 8, order: 0 }, { targetReps: 8, order: 1 }, { targetReps: 8, order: 2 }] },
      { exercise: findExercise('lat') || findExercise('pulldown'), weight: 100, sets: [{ targetReps: 10, order: 0 }, { targetReps: 10, order: 1 }] },
      { exercise: findExercise('bicep') || findExercise('curl'), weight: 30, sets: [{ targetReps: 12, order: 0 }, { targetReps: 12, order: 1 }] }
    ];

    for (let i = 0; i < pullExercises.length; i++) {
      const ex = pullExercises[i];
      if (ex.exercise) {
        await db.trainingDayExercises.add({
          trainingDayId: upperPullId,
          exerciseId: ex.exercise.id!,
          weight: ex.weight,
          sets: ex.sets,
          order: i,
          discoveryMode: false
        });
      }
    }

    // Training Day 3: Lower Body
    const lowerBodyId = await db.trainingDays.add({
      name: 'Lower Body',
      exercises: [],
      createdAt: now,
      updatedAt: now
    });

    const lowerExercises = [
      { exercise: findExercise('squat'), weight: 185, sets: [{ targetReps: 8, order: 0 }, { targetReps: 8, order: 1 }, { targetReps: 8, order: 2 }] },
      { exercise: findExercise('deadlift'), weight: 225, sets: [{ targetReps: 5, order: 0 }, { targetReps: 5, order: 1 }, { targetReps: 5, order: 2 }] },
      { exercise: findExercise('lunge'), weight: 25, sets: [{ targetReps: 12, order: 0 }, { targetReps: 12, order: 1 }] }
    ];

    for (let i = 0; i < lowerExercises.length; i++) {
      const ex = lowerExercises[i];
      if (ex.exercise) {
        await db.trainingDayExercises.add({
          trainingDayId: lowerBodyId,
          exerciseId: ex.exercise.id!,
          weight: ex.weight,
          sets: ex.sets,
          order: i,
          discoveryMode: false
        });
      }
    }

    // Training Day 4: Full Body
    const fullBodyId = await db.trainingDays.add({
      name: 'Full Body',
      exercises: [],
      createdAt: now,
      updatedAt: now
    });

    const fullBodyExercises = [
      { exercise: findExercise('squat'), weight: 155, sets: [{ targetReps: 10, order: 0 }, { targetReps: 10, order: 1 }] },
      { exercise: findExercise('bench') || findExercise('push'), weight: 115, sets: [{ targetReps: 10, order: 0 }, { targetReps: 10, order: 1 }] },
      { exercise: findExercise('row'), weight: 95, sets: [{ targetReps: 10, order: 0 }, { targetReps: 10, order: 1 }] }
    ];

    for (let i = 0; i < fullBodyExercises.length; i++) {
      const ex = fullBodyExercises[i];
      if (ex.exercise) {
        await db.trainingDayExercises.add({
          trainingDayId: fullBodyId,
          exerciseId: ex.exercise.id!,
          weight: ex.weight,
          sets: ex.sets,
          order: i,
          discoveryMode: false
        });
      }
    }

    // Training Day 5: Core & Cardio
    const coreCardioId = await db.trainingDays.add({
      name: 'Core & Cardio',
      exercises: [],
      createdAt: now,
      updatedAt: now
    });

    const coreExercises = [
      { exercise: findExercise('plank'), weight: 0, sets: [{ targetReps: 60, order: 0 }, { targetReps: 60, order: 1 }] },
      { exercise: findExercise('crunch') || findExercise('ab'), weight: 0, sets: [{ targetReps: 20, order: 0 }, { targetReps: 20, order: 1 }] },
      { exercise: findExercise('mountain') || findExercise('burpee'), weight: 0, sets: [{ targetReps: 15, order: 0 }, { targetReps: 15, order: 1 }] }
    ];

    for (let i = 0; i < coreExercises.length; i++) {
      const ex = coreExercises[i];
      if (ex.exercise) {
        await db.trainingDayExercises.add({
          trainingDayId: coreCardioId,
          exerciseId: ex.exercise.id!,
          weight: ex.weight,
          sets: ex.sets,
          order: i,
          discoveryMode: false
        });
      }
    }

    console.log('Successfully seeded 5 training days!');
  } catch (error) {
    console.error('Failed to seed training days:', error);
    throw error;
  }
}

export async function seedRoutines(): Promise<void> {
  try {
    // Check if routines already exist
    const existingCount = await db.routines.count();
    if (existingCount > 0) {
      console.log(`${existingCount} routines already exist. Skipping seed.`);
      return;
    }

    console.log('Seeding routines...');
    const now = new Date();

    // Get training days - if none exist, seed them first (which will also seed exercises if needed)
    let trainingDays = await db.trainingDays.toArray();
    if (trainingDays.length === 0) {
      console.log('No training days found. Seeding training days first...');
      await seedTrainingDays();
      trainingDays = await db.trainingDays.toArray();
    }

    // Helper to find training day by name
    const findTrainingDay = (name: string) => trainingDays.find(td => td.name.toLowerCase().includes(name.toLowerCase()));

    // Routine 1: Push/Pull/Legs Split
    const upperPush = findTrainingDay('upper body push');
    const upperPull = findTrainingDay('upper body pull');
    const lowerBody = findTrainingDay('lower body');

    if (upperPush && upperPull && lowerBody) {
      await db.routines.add({
        name: 'Push/Pull/Legs Split',
        trainingDayIds: [upperPush.id!, upperPull.id!, lowerBody.id!],
        currentDayIndex: 0,
        active: true,
        createdAt: now,
        updatedAt: now
      });
    }

    // Routine 2: Full Body 3x/Week
    const fullBody = findTrainingDay('full body');
    if (fullBody) {
      await db.routines.add({
        name: 'Full Body 3x/Week',
        trainingDayIds: [fullBody.id!, fullBody.id!, fullBody.id!],
        currentDayIndex: 0,
        active: false,
        createdAt: now,
        updatedAt: now
      });
    }

    // Routine 3: 5-Day Split
    const coreCardio = findTrainingDay('core');
    if (upperPush && upperPull && lowerBody && fullBody && coreCardio) {
      await db.routines.add({
        name: '5-Day Split',
        trainingDayIds: [upperPush.id!, upperPull.id!, lowerBody.id!, fullBody.id!, coreCardio.id!],
        currentDayIndex: 0,
        active: false,
        createdAt: now,
        updatedAt: now
      });
    }

    console.log('Successfully seeded 3 routines!');
  } catch (error) {
    console.error('Failed to seed routines:', error);
    throw error;
  }
}

export async function migrateExercisesWithMuscleGroups(): Promise<void> {
  try {
    console.log('Migrating exercises with muscle group data...');
    const exercises = await db.exercises.toArray();
    let updateCount = 0;
    
    for (const exercise of exercises) {
      // Find matching exercise in library
      const libraryExercise = exerciseLibrary.find(lib => lib.name === exercise.name);
      
      if (libraryExercise && (!exercise.primaryMuscles || exercise.primaryMuscles.length === 0)) {
        await db.exercises.update(exercise.id!, {
          primaryMuscles: libraryExercise.primaryMuscles,
          secondaryMuscles: libraryExercise.secondaryMuscles,
          updatedAt: new Date()
        });
        updateCount++;
      }
    }
    
    console.log(`Successfully migrated ${updateCount} exercises with muscle group data!`);
  } catch (error) {
    console.error('Failed to migrate exercises:', error);
    throw error;
  }
}

/**
 * DANGER ZONE: Migrate existing database to use consistent exercise IDs
 * This will preserve all workout history but reassign exercise IDs to match the library
 */
export async function migrateToConsistentExerciseIds(): Promise<void> {
  try {
    console.log('🚨 Starting exercise ID migration - this will preserve your workout history!');
    
    // Get all existing exercises and create ID mapping
    const existingExercises = await db.exercises.toArray();
    const idMapping = new Map<number, number>(); // oldId -> newId
    
    console.log(`Found ${existingExercises.length} existing exercises to migrate...`);
    
    // For each existing exercise, find its new consistent ID
    for (const exercise of existingExercises) {
      const libraryExercise = exerciseLibrary.find(lib => lib.name === exercise.name);
      
      if (libraryExercise) {
        // This exercise exists in library - map to consistent ID
        const oldId = exercise.id!;
        const newId = libraryExercise.id!;
        idMapping.set(oldId, newId);
        
        console.log(`Mapping "${exercise.name}": ${oldId} -> ${newId}`);
      } else {
        // Custom exercise - keep existing ID
        console.log(`Keeping custom exercise "${exercise.name}" with ID ${exercise.id}`);
      }
    }
    
    if (idMapping.size === 0) {
      console.log('No exercises need ID migration!');
      return;
    }
    
    console.log(`Migrating ${idMapping.size} exercise ID mappings...`);
    
    // Step 1: Update all TrainingDayExercise references
    const trainingDayExercises = await db.trainingDayExercises.toArray();
    for (const tde of trainingDayExercises) {
      const newId = idMapping.get(tde.exerciseId);
      if (newId) {
        await db.trainingDayExercises.update(tde.id!, { exerciseId: newId });
      }
    }
    
    // Step 2: Update all ExercisePerformance references  
    const performances = await db.exercisePerformances.toArray();
    for (const perf of performances) {
      const newId = idMapping.get(perf.exerciseId);
      if (newId) {
        await db.exercisePerformances.update(perf.id!, { exerciseId: newId });
      }
    }
    
    // Step 3: Replace exercises with consistent IDs
    const updatedExercises = existingExercises
      .map(exercise => {
        const libraryExercise = exerciseLibrary.find(lib => lib.name === exercise.name);
        if (libraryExercise) {
          // Replace with library version using consistent ID
          return {
            ...libraryExercise,
            universalWeight: exercise.universalWeight, // Preserve your progression data
            universalWeightType: exercise.universalWeightType,
            universalWeightUpdatedAt: exercise.universalWeightUpdatedAt,
            createdAt: exercise.createdAt, // Preserve original dates
            updatedAt: new Date()
          };
        } else {
          // Keep custom exercise as-is
          return exercise;
        }
      });
    
    // Step 4: Clear and replace all exercises
    await db.exercises.clear();
    await db.exercises.bulkPut(updatedExercises);
    
    console.log('✅ Migration complete! Your workout history is preserved with consistent exercise IDs.');
    console.log(`Migrated ${idMapping.size} exercises to consistent IDs`);
    
  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
}

export async function seedAllData(): Promise<void> {
  try {
    console.log('Seeding all workout data...');
    await seedExerciseLibrary();
    await seedTrainingDays();
    await seedRoutines();
    console.log('All data seeded successfully!');
  } catch (error) {
    console.error('Failed to seed all data:', error);
    throw error;
  }
}